// ============================================
// 계단 등록 제안 컴포넌트 (심플 HTML 스타일)
// ============================================
import React, { useState, useEffect } from 'react';
import { db, auth, storage } from '../firebase';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Leaflet 기본 아이콘 수정 (번들링 이슈 해결)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// 지도 클릭 이벤트 핸들러
function LocationMarker({ position, onLocationSelect }) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng);
    }
  });

  return position ? <Marker position={position} /> : null;
}

function StairProposal({ onClose, onRecordStair }) {
  const [stairName, setStairName] = useState('');
  const [steps, setSteps] = useState('');
  const [station, setStation] = useState('');
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [location, setLocation] = useState(null);
  const [submittedStairData, setSubmittedStairData] = useState(null);

  // 현재 위치 가져오기
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('위치 오류:', error);
        }
      );
    }
  }, []);

  // 사진 선택
  const handlePhotoChange = (e) => {
    if (e.target.files[0]) {
      setPhoto(e.target.files[0]);
    }
  };

  // 계단 기록하기
  const handleRecordNow = async () => {
    setMessage('기록 처리 중...');

    try {
      // 제안 문서에 즉시 기록 플래그 추가
      await updateDoc(doc(db, 'stair_proposals', submittedStairData.proposalId), {
        recordImmediately: true
      });

      setMessage('✅ 제안 완료! 자동 승인되면 바로 기록됩니다.');

      if (onRecordStair && submittedStairData) {
        onRecordStair(submittedStairData);
      }
    } catch (error) {
      console.error('기록 플래그 설정 실패:', error);
      setMessage('✅ 제안 완료!');
    }

    setTimeout(() => {
      onClose();
    }, 2000);
  };

  // 나중에 기록하기
  const handleRecordLater = () => {
    onClose();
  };

  // 제안 제출
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!location) {
      setMessage('위치 정보를 가져올 수 없습니다. 잠시 후 다시 시도해 주세요.');
      return;
    }

    if (!photo) {
      setMessage('계단 사진을 촬영해 주세요.');
      return;
    }

    setLoading(true);
    setMessage('계단 제안을 등록 중입니다.');

    try {
      // 사진 업로드
      const photoRef = ref(storage, `proposals/${auth.currentUser.uid}/${Date.now()}.jpg`);
      await uploadBytes(photoRef, photo);
      const photoUrl = await getDownloadURL(photoRef);

      // 제안 데이터 저장 (나중에 recordImmediately 플래그 추가 가능)
      const proposalRef = await addDoc(collection(db, 'stair_proposals'), {
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        stairName,
        steps: parseInt(steps, 10),
        station,
        photoUrl,
        lat: location.lat,
        lng: location.lng,
        status: 'pending',
        createdAt: new Date()
      });

      // 제안된 계단 정보 및 제안 ID 저장
      const stairData = {
        name: stairName,
        station: station,
        steps: parseInt(steps, 10),
        lat: location.lat,
        lng: location.lng,
        proposalId: proposalRef.id,
        photoUrl: photoUrl
      };
      setSubmittedStairData(stairData);

      // recordImmediately 플래그 설정 (자동 기록)
      await updateDoc(doc(db, 'stair_proposals', proposalRef.id), {
        recordImmediately: true
      });

      setMessage('✅ 등록 완료되었습니다!\n\n🔄 잠시 후 새로고침하고\n📝 방금 등록하신 계단을 기록해주세요!\n\n(약 10초 후 승인 완료)');

      // 3초 후 자동으로 닫기
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (error) {
      setMessage('제안 등록에 실패했습니다. 다시 시도해 주세요.');
      console.error(error);
    }
    setLoading(false);
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <button onClick={onClose} style={styles.closeButton}>닫기</button>

        {false ? (
          // 기록 여부 확인 프롬프트
          <div style={styles.promptContainer}>
            <h2 style={styles.promptTitle}>🎉 계단 제안 완료!</h2>
            <p style={styles.promptMessage}>
              {submittedStairData?.name}이(가) 성공적으로 등록되었습니다.
            </p>
            <p style={styles.promptSubMessage}>
              지금 이 계단을 오르셨나요?<br />
              바로 기록하시겠어요?
            </p>

            <div style={styles.promptButtons}>
              <button
                onClick={handleRecordNow}
                style={styles.recordButton}
              >
                ✅ 네, 지금 기록할게요!
              </button>
              <button
                onClick={handleRecordLater}
                style={styles.laterButton}
              >
                나중에 할게요
              </button>
            </div>

            <p style={styles.promptNote}>
              💡 자동 승인 시스템이 검토 중입니다.<br />
              조건을 충족하면 곧바로 사용 가능합니다!
            </p>
          </div>
        ) : (
          <>
            <h2 style={styles.title}>새 계단 등록 제안</h2>
            <p style={styles.desc}>
              새로 발견한 계단 정보를 남겨 주세요. 관리자 검토 후 계단 목록에 추가됩니다.
            </p>

            <form onSubmit={handleSubmit} style={styles.form}>
          {/* 계단 이름 */}
          <div style={styles.field}>
            <label style={styles.label}>계단 이름 *</label>
            <input
              type="text"
              placeholder="예: 신사역 3번 출구 계단"
              value={stairName}
              onChange={(e) => setStairName(e.target.value)}
              style={styles.input}
              required
            />
          </div>

          {/* 지하철역 */}
          <div style={styles.field}>
            <label style={styles.label}>지하철역 이름 *</label>
            <input
              type="text"
              placeholder="예: 신사역"
              value={station}
              onChange={(e) => setStation(e.target.value)}
              style={styles.input}
              required
            />
          </div>

          {/* 계단 수 */}
          <div style={styles.field}>
            <label style={styles.label}>계단 수 (개) *</label>
            <input
              type="number"
              placeholder="예: 120"
              value={steps}
              onChange={(e) => setSteps(e.target.value)}
              style={styles.input}
              required
              min="1"
            />
            <p style={styles.helpTextSmall}>
              가능한 한 정확하게 세어 보고 입력해 주세요.
            </p>
          </div>

          {/* 사진 */}
          <div style={styles.field}>
            <label style={styles.label}>계단 사진 *</label>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoChange}
              style={styles.fileInput}
              required
            />
            {photo && (
              <p style={styles.infoText}>선택된 파일: {photo.name}</p>
            )}
            <p style={styles.helpTextSmall}>
              실제 계단 모습이 잘 보이도록 촬영해 주세요.
            </p>
          </div>

          {/* 지도 */}
          <div style={styles.field}>
            <label style={styles.label}>계단 위치 (지도 클릭으로 수정 가능)</label>
            {location ? (
              <div style={styles.mapContainer}>
                <MapContainer
                  center={[location.lat, location.lng]}
                  zoom={16}
                  style={styles.map}
                  scrollWheelZoom={false}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <LocationMarker
                    position={[location.lat, location.lng]}
                    onLocationSelect={(latlng) => setLocation({ lat: latlng.lat, lng: latlng.lng })}
                  />
                </MapContainer>
                <div style={styles.locationInfo}>
                  위도: {location.lat.toFixed(6)}, 경도: {location.lng.toFixed(6)}
                </div>
              </div>
            ) : (
              <div style={styles.locationInfo}>
                위치 정보를 가져오는 중...
              </div>
            )}
            <p style={styles.helpTextSmall}>
              지도를 클릭하여 정확한 계단 위치를 지정할 수 있습니다.
            </p>
          </div>

          {/* 메시지 */}
          {message && (
            <div style={styles.message}>{message}</div>
          )}

          {/* 제출 버튼 */}
          <button 
            type="submit" 
            style={styles.submitButton}
            disabled={loading}
          >
            {loading ? '등록 중입니다...' : '제안 등록하기'}
          </button>
        </form>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================
// 스타일 (심플 HTML 느낌)
// ============================================
const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '16px',
    zIndex: 2000
  },
  modal: {
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    maxWidth: '480px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: '20px',
    position: 'relative',
    boxSizing: 'border-box'
  },
  closeButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    border: '1px solid #ccc',
    backgroundColor: '#f7f7ff',
    fontSize: '11px',
    padding: '4px 8px',
    cursor: 'pointer'
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    margin: '4px 0 4px 0'
  },
  desc: {
    fontSize: '13px',
    color: '#555',
    margin: '0 0 14px 0'
  },
  form: {
    fontSize: '13px'
  },
  field: {
    marginBottom: '14px'
  },
  label: {
    display: 'block',
    marginBottom: '4px',
    fontSize: '13px',
    fontWeight: '500'
  },
  input: {
    width: '100%',
    padding: '8px',
    fontSize: '13px',
    border: '1px solid #ccc',
    boxSizing: 'border-box'
  },
  fileInput: {
    width: '100%',
    padding: '6px 0',
    fontSize: '13px'
  },
  infoText: {
    marginTop: '6px',
    fontSize: '12px',
    color: '#555'
  },
  helpTextSmall: {
    marginTop: '4px',
    fontSize: '11px',
    color: '#777'
  },
  mapContainer: {
    border: '1px solid #ccc',
    overflow: 'hidden'
  },
  map: {
    height: '300px',
    width: '100%'
  },
  locationInfo: {
    padding: '8px',
    backgroundColor: '#f5f5f5',
    fontSize: '11px',
    color: '#555',
    borderTop: '1px solid #ddd'
  },
  message: {
    marginTop: '6px',
    marginBottom: '10px',
    padding: '8px',
    fontSize: '12px',
    backgroundColor: '#f1f8e9',
    border: '1px solid #c8e6c9'
  },
  submitButton: {
    width: '100%',
    padding: '10px 0',
    marginTop: '4px',
    border: '1px solid #000',
    backgroundColor: '#000',
    color: '#fff',
    fontSize: '13px',
    cursor: 'pointer'
  },
  promptContainer: {
    padding: '20px',
    textAlign: 'center'
  },
  promptTitle: {
    fontSize: '24px',
    fontWeight: '600',
    margin: '10px 0',
    color: '#333'
  },
  promptMessage: {
    fontSize: '15px',
    color: '#555',
    margin: '15px 0',
    fontWeight: '500'
  },
  promptSubMessage: {
    fontSize: '14px',
    color: '#666',
    margin: '20px 0',
    lineHeight: '1.6'
  },
  promptButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    margin: '25px 0'
  },
  recordButton: {
    width: '100%',
    padding: '14px 0',
    border: '2px solid #4CAF50',
    backgroundColor: '#4CAF50',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    borderRadius: '8px',
    transition: 'background-color 0.3s'
  },
  laterButton: {
    width: '100%',
    padding: '12px 0',
    border: '1px solid #999',
    backgroundColor: '#fff',
    color: '#666',
    fontSize: '14px',
    cursor: 'pointer',
    borderRadius: '8px'
  },
  promptNote: {
    fontSize: '11px',
    color: '#888',
    marginTop: '20px',
    padding: '10px',
    backgroundColor: '#f9f9f9',
    borderRadius: '5px',
    lineHeight: '1.5'
  }
};

export default StairProposal;
